import { useEffect, useMemo, useRef, useState } from 'react';
import maplibregl, { type Map as MlMap } from 'maplibre-gl';
import type { Boundary, MapRegion } from '../../shared/protocol';

/**
 * v27 BOLT-146 — pick your ground.
 *
 * Full screen, the way Draw custom already works, because Jake's complaint was
 * that the lobby map is "hard to see since it's small". Tap a region to take
 * it, tap again to give it back.
 *
 * THREE states, not the lab's five (see `v27-implementation-spec.md`). The lab
 * drew absorbed strips in their own colour so the absorb rule could be
 * debugged; a player cannot act on that difference, and showing it would be
 * implementation exposed as UI - which is what got the barrier checklist
 * killed. So a strip that came along renders as YOURS, because that is what it
 * is.
 *
 * Colour is never the only channel: stroke weight and dash separate yours from
 * available independently of hue, and the bone outline is the definitive "this
 * is your play area" line. That is also what makes this work for a colour-blind
 * host.
 */
export interface MapEditorProps {
  regions: MapRegion[];
  center: { lat: number; lng: number } | null;
  /** The server's own union, drawn as the definitive outline. */
  boundary?: Boundary | null;
  /** Fired with the new pick list. The SERVER decides what that resolves to. */
  onPick: (ids: string[]) => void;
  onDrawCustom: () => void;
  onDone: () => void;
  /** Set when the last pick was refused, so the host is told why. */
  notice?: string | null;
}

const FILL = {
  yours: '#e8451b', // --signal
  addable: '#5c7a6e', // --moss
  other: '#22272c', // --graphite-2
  outline: '#f5f1e6', // --bone
};

export function MapEditor(props: MapEditorProps) {
  const { regions, center, boundary } = props;
  const holder = useRef<HTMLDivElement | null>(null);
  const map = useRef<MlMap | null>(null);
  const [ready, setReady] = useState(false);

  /**
   * The host's OWN picks, and never `inPlay`.
   *
   * THE FEEDBACK LOOP THIS EXISTS TO BREAK. `inPlay` includes everything the
   * union swallowed - ponds, junction slivers - so building the pick list from
   * it sent all of them back as explicit picks on the very first tap. They then
   * came back marked `picked`, which is what draws the seam outline, and every
   * pond in the play area acquired a 1 px orange ring. Jake: "it's drawing pond
   * outlines on ios."
   *
   * The lab never had this because it tracks its own selection instead of
   * echoing the server's.
   *
   * The fallback matters during a deploy: `picked` is additive and optional, so
   * a server that predates it sends none at all, and an empty pick list would
   * wipe the map on the next tap.
   */
  const picked = useMemo(() => {
    const anyPicked = regions.some((r) => r.picked !== undefined);
    return regions.filter((r) => (anyPicked ? r.picked : r.inPlay)).map((r) => r.id);
  }, [regions]);

  const data = useMemo(
    () => ({
      type: 'FeatureCollection' as const,
      features: regions.map((r) => ({
        type: 'Feature' as const,
        // `seam` drives the OUTLINE and is the host's own pick; `yours` drives
        // the fill and is everything inside the play area. Absorbed glue and
        // swallowed ponds are yours to look at, not yours to act on.
        properties: { id: r.id, yours: r.inPlay, seam: !!r.picked, addable: r.addable && !r.inPlay, block: r.block },
        geometry: { type: 'Polygon' as const, coordinates: [r.ring] },
      })),
    }),
    [regions],
  );

  useEffect(() => {
    if (!holder.current || map.current) return;
    let cancelled = false;
    (async () => {
      const style = await fetch('/api/mapstyle')
        .then((r) => r.json())
        .catch(() => ({ version: 8, sources: {}, layers: [] }));
      if (cancelled || !holder.current) return;
      const m = new maplibregl.Map({
        container: holder.current,
        style,
        center: center ? [center.lng, center.lat] : [0, 0],
        zoom: 13,
        minZoom: 9,
      });
      map.current = m;
      // Idempotent and hung off `styledata` as well as `load`: a backgrounded
      // tab never renders, so `load` may never fire while the style is already
      // there, and then no layer is added and nothing draws with no error.
      const setup = () => {
        if (m.getLayer('regions-fill')) return;
        m.addSource('regions', { type: 'geojson', data });
        m.addSource('regions-union', { type: 'geojson', data: EMPTY });
        // THE PLAY AREA IS THE UNION, NOT THE FACES. Painting it per-face was
        // wrong in both directions at once: where no face exists (a junction
        // the arrangement does not enumerate) nothing was drawn at all, and
        // where faces NEST - a pond inside a block, because `allFacesAround`
        // returns outer rings so the block still covers the pond's ground -
        // two fills stacked and the pond came out brighter than its
        // surroundings. The union cannot disagree with the boundary: it IS the
        // boundary, and it is what decides a penalty.
        m.addLayer({
          id: 'regions-union-fill',
          type: 'fill',
          source: 'regions-union',
          paint: { 'fill-color': FILL.yours, 'fill-opacity': 0.28 },
        });
        m.addLayer({
          id: 'regions-fill',
          type: 'fill',
          source: 'regions',
          paint: {
            'fill-color': ['case', ['get', 'yours'], FILL.yours, ['get', 'addable'], FILL.addable, FILL.other],
            // 0 for yours: the union above paints the play area, exactly once.
            'fill-opacity': ['case', ['get', 'yours'], 0, ['get', 'addable'], 0.2, 0.1],
          },
        });
        // Two line layers, because `line-dasharray` is the one paint property
        // that rejects a data expression: MapLibre throws from inside addLayer
        // and silently takes every layer after it with it.
        m.addLayer({
          id: 'regions-yours',
          type: 'line',
          source: 'regions',
          filter: ['==', ['get', 'seam'], true],
          // Jake, on the shipped 2 px: "the lines showing the barriers within the map
          // (orange lines) are a bit thick ... it's confusing as to what that means." The
          // seams between regions you ALREADY own are edit structure, not a barrier the
          // player must respect, so they stay subordinate to the bone play-area edge -
          // which IS the line that means something. He picked thin: 1 px at 60%.
          paint: { 'line-color': FILL.yours, 'line-width': 1, 'line-opacity': 0.6 },
        });
        m.addLayer({
          id: 'regions-addable',
          type: 'line',
          source: 'regions',
          filter: ['==', ['get', 'addable'], true],
          paint: { 'line-color': FILL.addable, 'line-width': 1.2, 'line-dasharray': [2, 2] },
        });
        m.addLayer({
          id: 'regions-outline',
          type: 'line',
          source: 'regions-union',
          paint: { 'line-color': FILL.outline, 'line-width': 3 },
        });
        m.getCanvas().style.cursor = 'crosshair';
        m.on('click', (e) => {
          const hit = m.queryRenderedFeatures(e.point, { layers: ['regions-fill'] });
          if (!hit.length) return;
          const p = hit[0].properties as { id: string; block: boolean; addable: boolean; yours: boolean };
          onTap(p);
        });
        setReady(true);
      };
      m.on('styledata', setup);
      m.on('load', setup);
      m.on('idle', setup);
      if (m.isStyleLoaded()) setup();
    })();
    return () => {
      cancelled = true;
      // MUST destroy it. A MapLibre instance that is merely unmounted keeps its
      // WebGL context, its workers and its tile fetches alive, and the editor
      // is opened once per round in the E2E suite: the leak showed up as later
      // specs timing out on a wall clock, not as anything pointing at a map.
      map.current?.remove();
      map.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the sources in step with the server's answer.
  const framed = useRef(false);
  useEffect(() => {
    const m = map.current;
    if (!m || !ready) return;
    (m.getSource('regions') as maplibregl.GeoJSONSource | undefined)?.setData(data);
    (m.getSource('regions-union') as maplibregl.GeoJSONSource | undefined)?.setData(
      (boundary as unknown as GeoJSON.Feature) ?? EMPTY,
    );
    // Frame ONCE, on the first payload. Re-framing on every pick would yank the
    // camera away from the edge the host is working along.
    if (!framed.current && regions.some((r) => r.inPlay)) {
      framed.current = true;
      fitTo(m, regions.filter((r) => r.inPlay));
    }
  }, [data, ready, regions, boundary]);

  const pickedRef = useRef(picked);
  pickedRef.current = picked;
  const onTap = (p: { id: string; block: boolean; addable: boolean; yours: boolean }) => {
    // A glue strip is not a choice, and since v27 renders it the same as its
    // neighbours the host cannot see that. So it has to SAY so on tap rather
    // than doing nothing (spec acceptance 4c).
    if (!p.block && !p.yours) return setLocal('That piece joins on its own once both sides are in.');
    // Removing the LAST region clears the map on purpose (Jake): a host setting
    // up at home who wants to play a block down the road clears first, then
    // picks. The server decides whether a removal splits the area; emptying it
    // is not a split.
    if (p.yours) return props.onPick(pickedRef.current.filter((x) => x !== p.id));
    if (!p.addable) return setLocal('That area does not connect to yours yet.');
    props.onPick([...pickedRef.current, p.id]);
  };

  const [local, setLocal] = useState<string | null>(null);
  useEffect(() => {
    if (!local) return;
    const t = setTimeout(() => setLocal(null), 2600);
    return () => clearTimeout(t);
  }, [local]);

  // Jake, on the server-side harvest: "The latency is fine as long as we show
  // some sort of loading indicator message, and the user also still has the
  // option to draw their own before awaiting the load." Both halves matter -
  // Draw custom is on screen from the first frame.
  const anyYours = regions.some((r) => r.inPlay);
  const hint = !regions.length
    ? 'Loading the areas around you…'
    : anyYours
      ? 'Tap an area to add it. Tap it again to remove it.'
      : 'Tap any area to start your map.';
  const notice = props.notice ?? local;

  return (
    <div className="draw-screen" data-testid="map-editor">
      <div className="map-full" ref={holder} data-testid="map-editor-canvas" />

      <div
        className={`editor-topbar${notice ? ' editor-topbar--notice' : ''}`}
        data-testid="map-editor-hint"
      >
        {notice ?? 'Tap an area to add it. Tap it again to remove it.'}
      </div>

      <div className="editor-actions">
        <button className="btn" data-testid="editor-draw-custom" onClick={props.onDrawCustom}>
          Draw custom
        </button>
        <button className="btn btn--signal" data-testid="editor-done" onClick={props.onDone}>
          Done
        </button>
      </div>
    </div>
  );
}

const EMPTY = { type: 'FeatureCollection' as const, features: [] };

/** Fit the camera to the play area, so a host sees the shape, not a corner. */
export function fitTo(m: MlMap, regions: readonly MapRegion[]): void {
  const rings = regions.map((r) => r.ring).filter((r) => r.length > 2);
  if (!rings.length || !m.fitBounds) return;
  let w = 180;
  let s = 90;
  let e = -180;
  let n = -90;
  for (const ring of rings) {
    for (const [x, y] of ring) {
      if (x < w) w = x;
      if (x > e) e = x;
      if (y < s) s = y;
      if (y > n) n = y;
    }
  }
  if (w > e || s > n) return;
  m.fitBounds(
    [
      [w, s],
      [e, n],
    ],
    { padding: 40, animate: false, maxZoom: 16 },
  );
}
